import {
  useMutation,
  type MutationObserverOptions,
  type MutateOptions,
  useQueryClient,
  type QueryKey,
} from "@tanstack/vue-query";
import type { AxiosResponse } from "axios";
import { djangoViewsetErrorHandler, type TVeeSetErrors } from "@/utils/DjangoError";
import type { ESubjects, TPermissionAction } from "@/api/models/auth/ability";
import type { OneOrManyOrNull } from "@/types/general";
import { useAbility } from "@casl/vue";
import { computed, unref, type MaybeRefOrGetter } from "vue";

export type TMutationPermissions = Partial<
  Record<TPermissionAction, MaybeRefOrGetter<OneOrManyOrNull<ESubjects>>>
>;

export type TMutationPermissionsMode = "all" | "any";

export type TMutationComposableParams<TVariables = any> = {
  veeSetErrors?: TVeeSetErrors;
  enabled?: MaybeRefOrGetter<boolean>;
  /** Each entry is one CASL check; "all" requires every check, "any" requires at least one. Default "all". */
  permissionsMode?: MaybeRefOrGetter<TMutationPermissionsMode>;
  permissions?: TMutationPermissions;
  neededKey?: (keyof TVariables)[];
};

export type TGenericMutationOptions<TData, TVariables = Partial<TData>, TContext = unknown> = {
  mutationFn: (variables: TVariables) => Promise<AxiosResponse<TData> | TData>;
  feature?: string;
  params?: TMutationComposableParams<TVariables>;
  invalidates?: QueryKey[] | ((variables: TVariables) => QueryKey[]);
  refetches?: QueryKey[] | ((variables: TVariables) => QueryKey[]);
  removes?: QueryKey[] | ((variables: TVariables) => QueryKey[]);
  sets?: QueryKey[] | ((data: TData, variables: TVariables) => QueryKey[]);
  options?: Omit<
    MutationObserverOptions<TData, Error, TVariables, TContext>,
    "mutationFn" | "onError" | "onSuccess"
  > & {
    onError?: (error: Error, variables: TVariables, context: TContext | undefined) => void;
    onSuccess?: (data: TData, variables: TVariables, context: TContext | undefined) => void;
  };
};

class CallbackError extends Error {
  constructor(public readonly cause: unknown) {
    super("Callback error");
    this.name = "CallbackError";
  }
}

export const useGenericMutation = <TData, TVariables = Partial<TData>, TContext = unknown>(
  options: TGenericMutationOptions<TData, TVariables, TContext>
) => {
  const { can } = useAbility();
  const queryClient = useQueryClient();

  const permissionsCheck = computed(() => {
    const checks: Partial<Record<TPermissionAction, boolean>> = {};
    if (options.params?.permissions) {
      for (const [action, subject] of Object.entries(options.params.permissions)) {
        const sub = unref(subject as MaybeRefOrGetter<OneOrManyOrNull<ESubjects>>);
        checks[action as TPermissionAction] = sub ? can(action, sub) : true;
      }
    }
    return checks;
  });

  const hasPermission = computed(() => {
    const checks = Object.values(permissionsCheck.value);
    if (checks.length === 0) return true;
    const mode = unref(options.params?.permissionsMode ?? "all");
    return mode === "any" ? checks.some((hasPerm) => hasPerm) : checks.every((hasPerm) => hasPerm);
  });

  // enabled is now a function of variables if neededKey is used
  const isSetupEnabled = computed(() => {
    const isEnabled = unref(options.params?.enabled ?? true);
    return isEnabled && hasPermission.value;
  });

  const mutation = useMutation<TData, Error, TVariables, TContext>({
    ...options.options,
    mutationFn: async (variables) => {
      // Dynamic permissions check
      if (!isSetupEnabled.value) {
        return Promise.reject(new Error("You do not have permission to perform this action."));
      }

      // Check needed properties if provided
      const neededKey = unref(options.params?.neededKey);
      if (neededKey && neededKey.length) {
        const missingKeys = neededKey.filter((key) => variables[key] === undefined);
        if (missingKeys.length > 0) {
          return Promise.reject(new Error(`Missing required keys: ${missingKeys.join(", ")}`));
        }
      }

      const response = await options.mutationFn(variables);
      if (
        response &&
        typeof response === "object" &&
        "data" in response &&
        "headers" in response &&
        "config" in response
      ) {
        return (response as AxiosResponse<TData>).data;
      }
      return response as TData;
    },
    onError: (error, variables, context) => {
      try {
        if (options.feature) {
          logError(error, {
            context: {
              feature: options.feature,
              step: "onError",
            },
          });
        }
        djangoViewsetErrorHandler(undefined, options.params?.veeSetErrors)(error);

        if (options.options?.onError) {
          options.options.onError(error, variables, context);
        }
      } catch (e) {
        throw new CallbackError(e);
      }
    },
    onSuccess: (data, variables, context) => {
      try {
        if (options.options?.onSuccess) {
          options.options.onSuccess(data, variables, context);
        }
      } catch (e) {
        throw new CallbackError(e);
      }
    },
  });

  const runInvalidations = async (data: TData, variables: TVariables) => {
    if (options.sets) {
      const keys =
        typeof options.sets === "function" ? options.sets(data, variables) : options.sets;
      keys.forEach((queryKey) => {
        queryClient.setQueryData(queryKey, data);
      });
    }
    if (options.removes) {
      const keys =
        typeof options.removes === "function" ? options.removes(variables) : options.removes;
      keys.forEach((queryKey) => {
        queryClient.removeQueries({ queryKey });
      });
    }
    if (options.invalidates) {
      const keys =
        typeof options.invalidates === "function"
          ? options.invalidates(variables)
          : options.invalidates;
      // this will make it so that the mutation is finished but the queries have not finished fetching
      // so it will be left on the individual query's loading state
      keys.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
    }
    if (options.refetches) {
      const keys =
        typeof options.refetches === "function" ? options.refetches(variables) : options.refetches;
      // Awaited, unlike the others: the point of a refetch is to have the data in hand before
      // whatever runs next (an `onSuccess` that navigates, typically).
      await Promise.allSettled(keys.map((queryKey) => queryClient.refetchQueries({ queryKey })));
    }
  };

  // READY FOR VERSION 2

  const mutate = (
    variables: TVariables,
    callOptions?: MutateOptions<TData, Error, TVariables, TContext> & { allowConcurrent?: boolean }
  ): void => {
    if (mutation.isPending.value && !callOptions?.allowConcurrent) return;
    const { onSuccess: callOnSuccess, ...restCallOptions } = callOptions ?? {};
    mutation.mutate(variables, {
      ...restCallOptions,
      onSuccess: (data, vars) => {
        // `mutate` is sync by contract, so the refetches inside are not awaited here — a caller
        // that needs the data before proceeding uses `mutateAsync`.
        void runInvalidations(data, vars);
        (callOnSuccess as ((data: TData, variables: TVariables) => void) | undefined)?.(data, vars);
      },
    });
  };

  const mutateAsync = async (
    variables: TVariables,
    callOptions?: MutateOptions<TData, Error, TVariables, TContext> & {
      throwOnError?: boolean;
      skipInvalidations?: boolean;
      allowConcurrent?: boolean;
    }
  ): Promise<TData | undefined> => {
    if (mutation.isPending.value && !callOptions?.allowConcurrent) return undefined;
    const {
      onSuccess: callOnSuccess,
      throwOnError,
      skipInvalidations,
      allowConcurrent: _allowConcurrent,
      ...restCallOptions
    } = callOptions ?? {};

    let result: TData | undefined;
    try {
      result = await mutation.mutateAsync(variables, restCallOptions);
    } catch (e) {
      if (e instanceof CallbackError) throw e.cause;
      // onError already handled logging and user feedback
      if (throwOnError) throw e;
      return undefined;
    }

    if (!skipInvalidations) await runInvalidations(result, variables);
    if (callOnSuccess) {
      await (callOnSuccess as (data: TData, variables: TVariables) => void | Promise<void>)(
        result,
        variables
      );
    }

    return result;
  };

  return {
    ...mutation,
    mutate,
    mutateAsync,
    isEnabled: isSetupEnabled,
    hasPermission,
    permissions: permissionsCheck,
  };
};
